import { useEffect, useRef, useState } from "react";
import { api, streamSSE } from "@/lib/api";
import { PaperPlaneTilt, Robot, Sparkle, Plus } from "@phosphor-icons/react";
import { motion } from "framer-motion";

const SUGGESTIONS = [
  "What VAT rate applies to medical device consultancy?",
  "Is a working lunch with a client deductible under Irish Revenue?",
  "Explain BIK on company phones for 2026",
  "What are eligible costs under Horizon Europe RIA?",
  "Do I need a DPIA for a health data platform?",
];

export default function Advisor() {
  const [sessions, setSessions] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const scrollRef = useRef(null);

  const loadSessions = async () => { const r = await api.get("/ai/chat/sessions"); setSessions(r.data); };
  useEffect(() => { loadSessions(); }, []);

  useEffect(() => {
    (async () => {
      if (!sessionId) { setMessages([]); return; }
      const r = await api.get(`/ai/chat/history?session_id=${sessionId}`);
      setMessages(r.data);
    })();
  }, [sessionId]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, streaming]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || streaming) return;
    setInput("");
    setMessages(m => [...m, { role: "user", content: msg, msg_id: `u-${Date.now()}` }, { role: "assistant", content: "", msg_id: `a-${Date.now()}` }]);
    setStreaming(true);
    let assistant = "";
    try {
      await streamSSE("/ai/chat", { session_id: sessionId, message: msg },
        (delta) => {
          assistant += delta;
          setMessages(m => {
            const copy = [...m];
            copy[copy.length - 1] = { ...copy[copy.length - 1], content: assistant };
            return copy;
          });
        },
        (sid) => {
          if (sid && !sessionId) setSessionId(sid);
          loadSessions();
        }
      );
    } catch (e) {
      setMessages(m => [...m.slice(0,-1), { role: "assistant", content: `Error: ${e.message}`, msg_id: `err-${Date.now()}` }]);
    } finally {
      setStreaming(false);
    }
  };

  return (
    <div className="flex gap-6 h-[calc(100vh-8rem)]">
      {/* Sessions */}
      <aside className="w-64 shrink-0 glass rounded-xl p-4 hidden md:flex flex-col">
        <button data-testid="btn-new-chat" onClick={() => { setSessionId(null); setMessages([]); }} className="btn-ghost text-sm mb-3 justify-center"><Plus size={14} weight="bold"/> New chat</button>
        <div className="eyebrow mb-2 px-2">Recent</div>
        <div className="flex-1 overflow-y-auto space-y-1">
          {sessions.length === 0 && <div className="text-white/40 text-xs px-2 py-4">No conversations yet.</div>}
          {sessions.map(s => (
            <button key={s.session_id} onClick={() => setSessionId(s.session_id)}
              className={`w-full text-left px-3 py-2 rounded-lg text-xs transition-colors ${sessionId === s.session_id ? "bg-white/8 text-white" : "text-white/60 hover:bg-white/5 hover:text-white"}`}
              data-testid={`chat-session-${s.session_id}`}>
              <div className="truncate">{s.last_message}</div>
              <div className="text-[10px] text-white/40 mt-1">{s.count} messages</div>
            </button>
          ))}
        </div>
      </aside>

      {/* Chat */}
      <div className="flex-1 flex flex-col glass rounded-xl overflow-hidden">
        <div className="px-6 py-4 border-b border-white/5 flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: "linear-gradient(135deg,#FFB020,#E59E1C)" }}>
            <Robot size={18} weight="fill" color="#050B0A" />
          </div>
          <div>
            <div className="font-display">Compliance Advisor</div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-white/50">Claude 4.5 · GDPR & Revenue.ie expert</div>
          </div>
        </div>

        <div ref={scrollRef} className="flex-1 overflow-y-auto p-6 space-y-4">
          {messages.length === 0 ? (
            <div className="max-w-xl mx-auto text-center py-12">
              <Sparkle size={28} weight="fill" color="#FFB020" className="mx-auto mb-4"/>
              <div className="font-display text-2xl mb-2">Ask about Irish compliance.</div>
              <div className="text-white/50 text-sm mb-6">VAT · BIK · Grant eligibility · GDPR · R&D credits — no accountant on speed-dial required.</div>
              <div className="grid gap-2">
                {SUGGESTIONS.map((s, i) => (
                  <button key={s} onClick={() => send(s)} className="text-left text-sm text-white/70 hover:text-white bg-white/3 hover:bg-white/6 border border-white/8 rounded-lg px-4 py-3 transition" data-testid={`suggest-${i}`}>
                    {s}
                  </button>
                ))}
              </div>
            </div>
          ) : messages.map((m,i) => (
            <motion.div key={m.msg_id || i} initial={{opacity:0,y:6}} animate={{opacity:1,y:0}}
              className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[75%] rounded-2xl px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap ${m.role === "user" ? "bg-[#FFB020] text-[#050B0A] font-medium" : "bg-white/5 text-white/90 border border-white/8"}`}>
                {m.content || (streaming && i === messages.length - 1 ? <><span className="dot-flash"/><span className="dot-flash"/><span className="dot-flash"/></> : "")}
              </div>
            </motion.div>
          ))}
        </div>

        <form onSubmit={(e) => { e.preventDefault(); send(); }} className="p-4 border-t border-white/5 flex gap-2">
          <input value={input} onChange={e => setInput(e.target.value)} placeholder="Ask about VAT, BIK, GDPR, grants…" disabled={streaming} data-testid="input-chat"/>
          <button type="submit" disabled={streaming || !input.trim()} className="btn-gold shrink-0" data-testid="btn-send-chat">
            <PaperPlaneTilt size={16} weight="fill"/>
          </button>
        </form>
      </div>
    </div>
  );
}
